/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Plus, X, ChevronLeft, ChevronRight, Eye, Clock, Trash2, Upload } from 'lucide-react';
import { getAvatarUrl } from '../utils/helpers';
import './StoriesPage.css';

const STORY_DURATION = 5000; // 5 seconds per story

export default function StoriesPage() {
  const { user }  = useAuth();
  const toast     = useToast();
  const fileRef   = useRef(null);

  const [stories,      setStories]      = useState([]);
  const [viewing,      setViewing]      = useState(null); // { userIndex, storyIndex }
  const [progress,     setProgress]     = useState(0);
  const [paused,       setPaused]       = useState(false);
  const [creating,     setCreating]     = useState(false);
  const [storyText,    setStoryText]    = useState('');
  const [storyBg,      setStoryBg]      = useState('#6C63FF');
  const [storyMedia,   setStoryMedia]   = useState(null);
  const [uploading,    setUploading]    = useState(false);

  const progressTimer = useRef(null);
  const myId = user?._id || user?.id;

  // Load stories
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('nexa_stories') || '[]');
      // Remove expired stories (>24h)
      const fresh = saved.filter(s => Date.now() - new Date(s.createdAt).getTime() < 24*60*60*1000);
      if (fresh.length !== saved.length) localStorage.setItem('nexa_stories', JSON.stringify(fresh));
      setStories(fresh);
    } catch { setStories([]); }
  }, []);

  const saveStories = (list) => {
    localStorage.setItem('nexa_stories', JSON.stringify(list));
    setStories(list);
  };

  // Group stories by user
  const grouped = stories.reduce((acc, s) => {
    const key = s.userId;
    if (!acc[key]) acc[key] = { userId:s.userId, userName:s.userName, userAvatar:s.userAvatar, stories:[] };
    acc[key].stories.push(s);
    return acc;
  }, {});

  // Put my stories first
  const userGroups = Object.values(grouped).sort((a,b) => a.userId===myId?-1:b.userId===myId?1:0);

  // My stories group
  const myGroup = grouped[myId];

  // ── Story viewer ──────────────────────────────────────────────
  const startViewing = (userIndex, storyIndex=0) => {
    setViewing({ userIndex, storyIndex });
    setProgress(0);
  };

  const closeViewer = () => { setViewing(null); setProgress(0); clearInterval(progressTimer.current); };

  const nextStory = () => {
    if (!viewing) return;
    const group = userGroups[viewing.userIndex];
    if (viewing.storyIndex < group.stories.length - 1) {
      setViewing(v => ({...v, storyIndex: v.storyIndex+1})); setProgress(0);
    } else if (viewing.userIndex < userGroups.length - 1) {
      setViewing({ userIndex: viewing.userIndex+1, storyIndex:0 }); setProgress(0);
    } else { closeViewer(); }
  };

  const prevStory = () => {
    if (!viewing) return;
    if (viewing.storyIndex > 0) {
      setViewing(v => ({...v, storyIndex: v.storyIndex-1})); setProgress(0);
    } else if (viewing.userIndex > 0) {
      const prevGroup = userGroups[viewing.userIndex-1];
      setViewing({ userIndex: viewing.userIndex-1, storyIndex: prevGroup.stories.length-1 }); setProgress(0);
    }
  };

  // Auto-advance
  useEffect(() => {
    if (!viewing || paused) return;
    clearInterval(progressTimer.current);
    const step = 100 / (STORY_DURATION / 100);
    progressTimer.current = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { clearInterval(progressTimer.current); nextStory(); return 0; }
        return p + step;
      });
    }, 100);
    return () => clearInterval(progressTimer.current);
  }, [viewing, paused]);

  // Mark as viewed
  useEffect(() => {
    if (!viewing) return;
    const group = userGroups[viewing.userIndex];
    if (!group) return;
    const story = group.stories[viewing.storyIndex];
    if (!story || story.viewedBy?.includes(myId)) return;
    const updated = stories.map(s => s.id===story.id
      ? {...s, viewedBy:[...(s.viewedBy||[]), myId]}
      : s);
    saveStories(updated);
  }, [viewing]);

  // Keyboard nav
  useEffect(() => {
    if (!viewing) return;
    const handler = (e) => {
      if (e.key==='ArrowRight') nextStory();
      if (e.key==='ArrowLeft')  prevStory();
      if (e.key==='Escape')     closeViewer();
      if (e.key===' ')          setPaused(p=>!p);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [viewing]);

  // ── Create story ──────────────────────────────────────────────
  const handleFileUpload = (file) => {
    if (!file) return;
    if (file.size > 20*1024*1024) { toast.error('Max 20MB'); return; }
    const reader = new FileReader();
    reader.onload = (e) => setStoryMedia({ dataUrl: e.target.result, type: file.type.startsWith('video')?'video':'image' });
    reader.readAsDataURL(file);
  };

  const publishStory = () => {
    if (!storyText.trim() && !storyMedia) { toast.error('Add text or media to your story'); return; }
    setUploading(true);
    const newStory = {
      id:          `story_${Date.now()}`,
      userId:      myId,
      userName:    user?.displayName,
      userAvatar:  user?.avatar || '',
      text:        storyText.trim(),
      bg:          storyBg,
      media:       storyMedia,
      createdAt:   new Date().toISOString(),
      expiresAt:   new Date(Date.now()+24*60*60*1000).toISOString(),
      viewedBy:    [myId],
      views:       0,
    };
    setTimeout(() => {
      saveStories([newStory, ...stories]);
      setCreating(false); setStoryText(''); setStoryMedia(null); setStoryBg('#6C63FF');
      setUploading(false);
      toast.success('Story published! 🎉');
    }, 600);
  };

  const deleteStory = (id) => {
    saveStories(stories.filter(s => s.id!==id));
    toast.info('Story deleted');
  };

  const timeLeft = (createdAt) => {
    const diff = 24*60*60*1000 - (Date.now() - new Date(createdAt).getTime());
    const h    = Math.floor(diff/3600000);
    const m    = Math.floor((diff%3600000)/60000);
    return h>0 ? `${h}h ${m}m left` : `${m}m left`;
  };

  const BG_COLORS = ['#6C63FF','#FF4D8D','#00B4D8','#00D68F','#FFB830','#8B5CF6','#EC4899','#F97316','#1a1a2e','#0f3460'];

  const currentGroup = viewing ? userGroups[viewing.userIndex] : null;
  const currentStory = currentGroup ? currentGroup.stories[viewing.storyIndex] : null;

  return (
    <div className="stories-page">

      {/* ── Header ── */}
      <div className="stories-header">
        <h1>Stories</h1>
        <p className="stories-subtitle">Moments that last 24 hours</p>
      </div>

      <div className="stories-body">

        {/* ── Your story ── */}
        <div className="stories-section">
          <h2 className="stories-section-title">Your Story</h2>
          <div className="stories-row">
            {/* Add story card */}
            <div className="story-add-card" onClick={() => setCreating(true)}>
              <div className="story-add-avatar">
                <img src={getAvatarUrl(user)} alt="" className="story-avatar-img"/>
                <div className="story-add-plus"><Plus size={16}/></div>
              </div>
              <p className="story-card-name">Add Story</p>
            </div>

            {/* My existing stories */}
            {myGroup?.stories.map((s,i) => (
              <div key={s.id} className="story-card" onClick={() => startViewing(userGroups.findIndex(g=>g.userId===myId), i)}>
                <div className="story-card-preview" style={{background:s.media?'#000':s.bg}}>
                  {s.media?.type==='image' && <img src={s.media.dataUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>}
                  {s.media?.type==='video' && <video src={s.media.dataUrl} style={{width:'100%',height:'100%',objectFit:'cover'}}/>}
                  {!s.media && s.text && <p className="story-card-text">{s.text.slice(0,60)}</p>}
                  <div className="story-card-gradient"/>
                </div>
                <div className="story-card-info">
                  <p className="story-card-name">Your story</p>
                  <p className="story-card-time"><Clock size={10}/> {timeLeft(s.createdAt)}</p>
                </div>
                <button className="story-delete-btn" onClick={e=>{e.stopPropagation();deleteStory(s.id);}}>
                  <Trash2 size={12}/>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Other stories ── */}
        {userGroups.filter(g => g.userId!==myId).length > 0 && (
          <div className="stories-section">
            <h2 className="stories-section-title">Recent Stories</h2>
            <div className="stories-row">
              {userGroups.filter(g => g.userId!==myId).map((group, i) => {
                const latest   = group.stories[group.stories.length-1];
                const allViewed = group.stories.every(s => (s.viewedBy||[]).includes(myId));
                return (
                  <div key={group.userId} className="story-card"
                    onClick={() => startViewing(userGroups.findIndex(g=>g.userId===group.userId))}>
                    <div className="story-card-preview" style={{background:latest.media?'#000':latest.bg}}>
                      {latest.media?.type==='image' && <img src={latest.media.dataUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>}
                      {!latest.media && latest.text && <p className="story-card-text">{latest.text.slice(0,60)}</p>}
                      <div className="story-card-gradient"/>
                      <div className={`story-ring ${allViewed?'viewed':''}`}>
                        <img src={getAvatarUrl({avatar:group.userAvatar})} alt="" className="story-ring-avatar"/>
                      </div>
                    </div>
                    <div className="story-card-info">
                      <p className="story-card-name">{group.userName}</p>
                      <p className="story-card-time"><Eye size={10}/> {group.stories.length} stor{group.stories.length>1?'ies':'y'}</p>
                    </div>
                    {!allViewed && <div className="story-unread-dot"/>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {stories.length===0 && (
          <div className="empty-state" style={{flex:1}}>
            <div style={{fontSize:'3rem',marginBottom:8}}>⭐</div>
            <h3>No stories yet</h3>
            <p>Be the first to share a moment!</p>
            <button className="btn btn-primary btn-sm" onClick={()=>setCreating(true)}>
              <Plus size={14}/> Create Story
            </button>
          </div>
        )}
      </div>

      {/* ── Story Viewer ── */}
      {viewing && currentStory && (
        <div className="story-viewer" onMouseDown={()=>setPaused(true)} onMouseUp={()=>setPaused(false)}>
          {/* Progress bars */}
          <div className="story-progress-bars">
            {currentGroup.stories.map((s,i) => (
              <div key={s.id} className="story-progress-track">
                <div className="story-progress-fill" style={{
                  width: i < viewing.storyIndex ? '100%' : i===viewing.storyIndex ? `${progress}%` : '0%',
                  transition: i===viewing.storyIndex ? 'none' : undefined
                }}/>
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="story-viewer-header">
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <img src={getAvatarUrl({avatar:currentGroup.userAvatar})} alt="" style={{width:38,height:38,borderRadius:'50%',border:'2px solid white'}}/>
              <div>
                <p style={{fontWeight:700,fontSize:'.9rem'}}>{currentGroup.userName}</p>
                <p style={{fontSize:'.72rem',opacity:.7}}>{timeLeft(currentStory.createdAt)}</p>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <span style={{fontSize:'.75rem',opacity:.7,display:'flex',alignItems:'center',gap:4}}>
                <Eye size={13}/> {(currentStory.viewedBy||[]).length}
              </span>
              {currentStory.userId===myId && (
                <button style={{background:'none',border:'none',color:'white',cursor:'pointer',opacity:.7}} onClick={()=>deleteStory(currentStory.id)}>
                  <Trash2 size={16}/>
                </button>
              )}
              <button style={{background:'none',border:'none',color:'white',cursor:'pointer',opacity:.8}} onClick={closeViewer}>
                <X size={20}/>
              </button>
            </div>
          </div>

          {/* Story content */}
          <div className="story-viewer-content" style={{background:currentStory.media?'#000':currentStory.bg}}>
            {currentStory.media?.type==='image' && (
              <img src={currentStory.media.dataUrl} alt="" style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}}/>
            )}
            {currentStory.media?.type==='video' && (
              <video src={currentStory.media.dataUrl} autoPlay muted loop style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}}/>
            )}
            {currentStory.text && (
              <div className="story-viewer-text">{currentStory.text}</div>
            )}
          </div>

          {/* Nav arrows */}
          <button className="story-nav-btn left" onClick={e=>{e.stopPropagation();prevStory();}}><ChevronLeft size={28}/></button>
          <button className="story-nav-btn right" onClick={e=>{e.stopPropagation();nextStory();}}><ChevronRight size={28}/></button>
        </div>
      )}

      {/* ── Create Story Modal ── */}
      {creating && (
        <div className="modal-overlay" onClick={()=>setCreating(false)}>
          <div className="modal-box story-create-modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Story</h2>
              <button className="btn-icon" onClick={()=>setCreating(false)}><X size={18}/></button>
            </div>

            {/* Preview */}
            <div className="story-create-preview" style={{background:storyMedia?'#000':storyBg}}>
              {storyMedia?.type==='image' && <img src={storyMedia.dataUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:12}}/>}
              {storyMedia?.type==='video' && <video src={storyMedia.dataUrl} controls style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:12}}/>}
              {storyText && <div className="story-preview-text">{storyText}</div>}
              {!storyMedia && !storyText && (
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8,color:'rgba(255,255,255,.5)',fontSize:'.875rem'}}>
                  <Plus size={32}/>
                  <span>Add text or photo</span>
                </div>
              )}
              {storyMedia && (
                <button style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,.5)',border:'none',borderRadius:'50%',width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',color:'white',cursor:'pointer'}}
                  onClick={()=>setStoryMedia(null)}><X size={14}/></button>
              )}
            </div>

            {/* Text input */}
            <textarea
              placeholder="What's on your mind?"
              value={storyText}
              onChange={e=>setStoryText(e.target.value)}
              maxLength={200}
              rows={3}
              style={{marginBottom:14,resize:'none'}}
            />

            {/* Background colors */}
            {!storyMedia && (
              <div style={{marginBottom:14}}>
                <p style={{fontSize:'.78rem',color:'var(--text-400)',marginBottom:8,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>Background</p>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {BG_COLORS.map(c=>(
                    <div key={c} style={{width:32,height:32,borderRadius:'50%',background:c,cursor:'pointer',border:`3px solid ${storyBg===c?'white':'transparent'}`,transition:'all .2s'}} onClick={()=>setStoryBg(c)}/>
                  ))}
                </div>
              </div>
            )}

            {/* Media upload */}
            <div style={{display:'flex',gap:10,marginBottom:16}}>
              <button className="btn btn-secondary w-full" onClick={()=>fileRef.current?.click()}>
                <Upload size={15}/> {storyMedia?'Change Media':'Add Photo/Video'}
              </button>
              {storyMedia && (
                <button className="btn btn-secondary" onClick={()=>setStoryMedia(null)} style={{flexShrink:0}}>
                  <X size={15}/>
                </button>
              )}
            </div>

            <input ref={fileRef} type="file" accept="image/*,video/*" style={{display:'none'}}
              onChange={e=>handleFileUpload(e.target.files[0])}/>

            <button className="btn btn-primary w-full" onClick={publishStory} disabled={uploading||(!storyText.trim()&&!storyMedia)}>
              {uploading ? <span className="loader" style={{width:16,height:16,borderWidth:2}}/> : '🚀 Share Story'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}